<script lang="ts">
import { defineComponent as DO_defineComponent } from 'vue';
export default /*#__PURE__*/ DO_defineComponent({
	name: 'ToTreeItem'
});
</script>
<template>
	<div class="to-tree-item">
		<div class="to-tree-content" :level="node.level" :class="[`level-${node.level}`, props.itemProps.toggleLink ? 'is-link' : '', node.isLeaf ? 'is-leaf' : '', node.isSelect ? 'is-on' : '']" @click.stop="handleItemClick(node)">
			<to-icon v-if="!node.isLeaf || node.isNeedLoad" class="to-tree-toggle" :link="!props.itemProps.toggleLink" :value="node.isExpand ? 'expand' : 'next'" @click.stop="handleExpand(node)"></to-icon>
			<to-icon v-if="node.isNeedLoad && node.loading" mode="tree-loading" link value="loading" />
			<slot :node="node">
				<div class="to-tree-label" :class="{ 'is-link': (props.itemProps.checkLink || props.itemProps.selectable) && !props.itemProps.toggleLink }">
					<to-checkbox v-if="itemProps.checkable" class="to-tree-check" :model-value="node.checked" @update:model-value="e => handleCheck(e, node)"></to-checkbox>
					<to-icon v-if="node.icon" class="to-tree-icon" :value="node.icon"></to-icon>
					<to-pic v-if="node.pic && !node.isSelect" class="to-tree-pic" :src="node.pic"></to-pic>
					<to-pic v-if="node.onPic && node.isSelect" class="to-tree-pic" :src="node.onPic"></to-pic>
					<div class="to-tree-label-text">{{ node[itemProps.labelText] }}</div>
				</div>
			</slot>
		</div>
		<template v-if="node[itemProps.childrenText] && node[itemProps.childrenText].length && !node.isLeaf">
			<div class="to-tree-child" :style="{ height: node.isExpand ? 'auto' : 0, overflow: 'hidden' }">
				<to-tree-item v-for="item in node[itemProps.childrenText]" :key="item[itemProps.valueText]" :node="item" :item-props="itemProps" is-child>
					<template #default="scope">
						<slot :node="scope.node"></slot>
					</template>
				</to-tree-item>
			</div>
		</template>
	</div>
</template>
<script setup lang="ts">
import { Node, Props, ProvideType } from './tree.vue'
import { inject, nextTick } from 'vue'
import { ToIcon, ToCheckbox } from '@touchvue/components'

const rootTree: ProvideType = inject('RootTree')



export interface ItemProps {
	node: Node
	itemProps: Props
	isChild?: boolean
}

const props = withDefaults(defineProps<ItemProps>(), {
	node: () => ({ label: '', value: '' }),
	itemProps: () => ({
		mode: 'default',
		expand: false,
		data: [],
		toggleLink: false,
		checkLink: false,
		checkable: false,
		selectable: false,
		checkStrictly: false,
		accordion: false,
		childrenText: 'children',
		valueText: 'value',
		labelText: 'label',
		load: null,
		parentSelectable: false
	}),
	isChild: false
})
async function handleExpand(node: Node) {
	if (props.itemProps.load && !node.isExpand && !(node[props.itemProps.childrenText] && node[props.itemProps.childrenText].length)) {
		rootTree.setPropertyByValue(node[props.itemProps.valueText], true, 'loading')
		props.itemProps
			.load(node)
			.then(syncChildren => {
				rootTree.setPropertyByValue(node[props.itemProps.valueText], false, 'loading')
				rootTree.setChildren(node, syncChildren)
			})
			.catch(err => {
				rootTree.setPropertyByValue(node[props.itemProps.valueText], false, 'loading')
				rootTree.setChildren(node, err, true)
			})
	} else {
		rootTree.setPropertyByValue(node[props.itemProps.valueText], !node.isExpand, 'isExpand')
	}
}

function handleCheck(e, node) {
	if (!props.itemProps.checkStrictly) {
		rootTree.setStopChange(true)
	}
	rootTree.setPropertyByValue(node[props.itemProps.valueText], e, 'checked')
	rootTree.change(node[props.itemProps.valueText], node)
	if (!props.itemProps.checkStrictly) {
		nextTick(() => {
			rootTree.setCheckByParentAndChild(rootTree.store.value, node[props.itemProps.valueText], e)
			setTimeout(() => {
				rootTree.setStopChange(false)
			}, 10)
		})
	}
}

function handleItemClick(node: Node) {
	if (!node.isLeaf && props.itemProps.toggleLink) {
		rootTree.setPropertyByValue(node[props.itemProps.valueText], !node.isExpand, 'isExpand')
	}
	if (props.itemProps.checkLink) {
		rootTree.setPropertyByValue(node[props.itemProps.valueText], !node.checked, 'checked')
		if (!props.itemProps.checkStrictly) {
			nextTick(() => {
				rootTree.setCheckByParentAndChild(rootTree.store.value, node[props.itemProps.valueText], !node.checked)
			})
		}
	}
	if (props.itemProps.selectable) {
		resetAllSelect(rootTree.store.value)
		rootTree.setStopChange(true)
		setOnlySelect(rootTree.store.value, node[props.itemProps.valueText])
		setTimeout(() => {
			rootTree.setStopChange(false)
		}, 10)
		nextTick(() => {
			rootTree.change(node[props.itemProps.valueText], node)
		})
	}
	rootTree.itemClick(node)
}

function resetAllSelect(arr) {
	arr.forEach(item => {
		if (item.isSelect) {
			item.isSelect = false
		} else {
			if (item[props.itemProps.childrenText] && item[props.itemProps.childrenText].length) {
				resetAllSelect(item[props.itemProps.childrenText])
			}
		}
	})
}

function setOnlySelect(arr, value) {
	arr.forEach(item => {
		if (item[props.itemProps.valueText] === value && (item.isLeaf || props.itemProps.parentSelectable) && props.itemProps.selectable) {
			item.isSelect = true
		} else {
			if (item[props.itemProps.childrenText] && item[props.itemProps.childrenText].length) {
				setOnlySelect(item[props.itemProps.childrenText], value)
			}
		}
	})
}
</script>
